/**
 * "Nova observação": a planilha é só leitura, então a observação vira um
 * comentário no histórico do ambiente escolhido — o mesmo histórico do Mapa
 * e das páginas do Legado (chave PEDIDO:AMBIENTE).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";

import AsyncButton from "../../../../components/AsyncButton";
import { useAuth } from "../../../../context/AuthContext";
import { comentarAmbiente } from "../../../../services/mapaProducao";
import { TAMANHO_COMENTARIO } from "../../../../components/HistoricoAmbiente";
import { chaveAmbiente, rotuloAmbiente } from "../../../MapaProducao/dados/chaves";
import { CURVA_ENTRADA, CURVA_SAIDA } from "../comum/movimento";

const PAGINA = "Clientes";

export default function NovaObservacao({ aberto, cliente, aoFechar, aoSalvar }) {
  const { request } = useAuth();
  const opcoes = useMemo(() => cliente.pedidos.flatMap((p) => p.ambientes)
    .map((a) => ({ chave: chaveAmbiente(a.row), rotulo: rotuloAmbiente(a.row), etapa: a.status }))
    .filter((o) => o.chave), [cliente]);
  const [chave, setChave] = useState("");
  const [texto, setTexto] = useState("");
  const campo = useRef(null);

  useEffect(() => {
    if (!aberto) return undefined;
    setChave((atual) => atual || opcoes[0]?.chave || "");
    const t = setTimeout(() => campo.current?.focus(), 60);
    const esc = (e) => e.key === "Escape" && aoFechar();
    document.addEventListener("keydown", esc);
    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", esc);
    };
  }, [aberto, opcoes, aoFechar]);

  const escolhida = opcoes.find((o) => o.chave === chave);

  return createPortal(
    <AnimatePresence>
      {aberto && (
        <motion.div className="cl-dialogo-fundo" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.14 } }} onClick={aoFechar}>
          <motion.div className="clg clg-dialogo" role="dialog" aria-modal="true" aria-labelledby="cl-nova-obs-t"
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1, transition: { duration: 0.24, ease: CURVA_ENTRADA } }}
            exit={{ opacity: 0, scale: 0.98, transition: { duration: 0.14, ease: CURVA_SAIDA } }}
            onClick={(e) => e.stopPropagation()}>
            <h2 id="cl-nova-obs-t">Nova observação</h2>
            <p>Fica no histórico do ambiente (o mesmo do Mapa da Produção); a planilha não muda.</p>
            <label>
              Ambiente
              <select value={chave} onChange={(e) => setChave(e.target.value)}>
                {opcoes.map((o) => <option key={o.chave} value={o.chave}>{o.rotulo}</option>)}
              </select>
            </label>
            <label>
              Observação
              <textarea ref={campo} value={texto} maxLength={TAMANHO_COMENTARIO} onChange={(e) => setTexto(e.target.value)}
                placeholder="O que a equipe precisa saber sobre este ambiente?" />
            </label>
            <div className="acoes">
              <button type="button" className="cl-btn" onClick={aoFechar}>Cancelar</button>
              <AsyncButton
                className="cl-btn pri"
                disabled={!texto.trim() || !escolhida}
                successMessage="Observação registrada no histórico."
                onClick={async () => {
                  await comentarAmbiente(request, {
                    chave: escolhida.chave, rotulo: escolhida.rotulo, texto: texto.trim(), etapa: escolhida.etapa, pagina: PAGINA,
                  });
                  setTexto("");
                  aoSalvar();
                }}
              >
                Salvar
              </AsyncButton>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
